'use client';

import { useRouter } from '@react-aria/utils';
import clsx from 'clsx';
import { useCallback, type ForwardedRef } from 'react';
import { useId, useObjectRef } from 'react-aria';
import { Breadcrumbs as RACBreadcrumbs, Breadcrumb as RACBreadcrumb } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconChevronRight } from '../../icons/index.js';
import { RouterProvider } from '../../providers/router/router-provider.js';
import { withStyleProps } from '../../with-style-props.js';
import type {
  AriaLabelingProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  FocusableElement,
  Href,
  Key,
  NavigationProps,
  RouterOptions,
  StringLikeChildren,
  WithRequired,
} from '../types.js';
import { Link } from '../typography/link.js';

export interface BreadcrumbsProps<T extends CollectionItem = CollectionItem>
  extends CommonProps, CollectionProps<T>, AriaLabelingProps {}

function Breadcrumbs<T extends CollectionItem = CollectionItem>(
  {
    children,
    items,
    UNSAFE_className,
    UNSAFE_style,

    // Aria labeling props
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    'aria-details': ariaDetails,
    ...props
  }: BreadcrumbsProps<T>,
  ref: ForwardedRef<HTMLElement>,
) {
  const listRef = useObjectRef(ref);
  const router = useRouter();
  const breadcrumbsId = useId();

  const navigationInterceptor = useCallback(
    (path: Href, routerOptions?: RouterOptions) => {
      // Breadcrumbs navigation causes the activated link to become disabled.
      // Blur the link beforehand so that the browser can decide where to place focus next.

      if (!listRef.current) {
        return;
      }

      const element = listRef.current.querySelector(`[href='${path}']`)!;
      (element as FocusableElement).blur();

      // We don't have access to the modifiers of the original event.
      // However, this function wouldn't run if the original modifiers impacted routing, so it is safe to pass an empty object.
      router.open(element, {}, path, routerOptions);
    },
    [router, listRef],
  );

  return (
    <RouterProvider navigate={navigationInterceptor}>
      <nav
        {...props}
        ref={listRef}
        // RAC puts a localized `aria-label` on `Breadcrumbs`, so we refer to it unless a custom label is provided
        aria-labelledby={ariaLabelledby ?? breadcrumbsId}
        aria-describedby={ariaDescribedby}
        aria-details={ariaDetails}
      >
        <RACBreadcrumbs
          id={breadcrumbsId}
          items={items}
          className={clsx('cim-breadcrumbs', UNSAFE_className)}
          style={UNSAFE_style}
          aria-label={ariaLabel}
        >
          {children}
        </RACBreadcrumbs>
      </nav>
    </RouterProvider>
  );
}

export interface BreadcrumbItemProps extends Omit<CommonProps, 'id'>, WithRequired<NavigationProps, 'href'> {
  /** The text to display as the breadcrumb. */
  children: StringLikeChildren;
  /** The ID of the item. Has to be unique across all items. */
  id?: Key;
}

/** Renders a single breadcrumb within `Breadcrumbs`. */
export function BreadcrumbItem({
  children,
  UNSAFE_className,
  UNSAFE_style,

  // Navigation props
  href,
  hrefLang,
  download,
  ping,
  referrerPolicy,
  rel,
  routerOptions,
  target,

  ...props
}: BreadcrumbItemProps) {
  return (
    <RACBreadcrumb {...props} className={clsx('cim-breadcrumb-item', UNSAFE_className)} style={UNSAFE_style}>
      <Link
        variant="body"
        href={href}
        hrefLang={hrefLang}
        download={download}
        ping={ping}
        referrerPolicy={referrerPolicy}
        rel={rel}
        routerOptions={routerOptions}
        target={target}
      >
        {children}
      </Link>
      <IconChevronRight size={16} />
    </RACBreadcrumb>
  );
}

BreadcrumbItem.displayName = 'BreadcrumbItem';

/**
 * Displays a list of breadcrumbs to help users navigate through a website.
 *
 * See [breadcrumbs usage guidelines](https://ui.cimpress.io/components/breadcrumbs/).
 */
const _Breadcrumbs = withStyleProps(forwardRef(Breadcrumbs), 'Breadcrumbs');

export { _Breadcrumbs as Breadcrumbs };
